/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { Deal } from '@/services/requests/housien-services/DealsService';
import type { DealPartyNames } from '../hooks/useDealPartyNames';
import { useDealClientRecord } from '../hooks/useDealClientRecord';
import { daysSince } from '../utils/deal-state';
import { latestProposal } from '../utils/deal-values';
import { DEAL_PANEL_CLASS, DealPanelLabel } from './DealPanel';

interface DealSummaryPanelProps {
  agencyId: string;
  deal: Deal;
  names: DealPartyNames;
  /** Already-localized label for the deal's capture channel. */
  originLabel: string;
}

/** One labelled fact in the grid. */
interface Fact {
  id: string;
  label: string;
  value: React.ReactNode;
}

/**
 * The payment arrangement, read off the standing offer's terms.
 *
 * The attendance itself never carries one: how a deal gets paid for is
 * agreed in an offer, so before there is an offer there is nothing to
 * state and the fact is left out rather than shown as a dash.
 *
 * @param deal - The attendance.
 * @param t - The deals-namespace translation function.
 * @returns The localized arrangement, or null when no offer states one.
 */
function paymentOf(deal: Deal, t: TFunction): string | null {
  const terms = latestProposal(deal)?.sale_terms ?? latestProposal(deal)?.rental_terms;
  const method = (terms as Record<string, any> | null | undefined)?.payment_method;
  if (!method) return null;
  return t(`detail.summary.payment.${method}`, { defaultValue: String(method) });
}

/**
 * The facts the panel states, skipping the ones nothing answers.
 */
function useFacts({ agencyId, deal, names, originLabel }: DealSummaryPanelProps): Fact[] {
  const { t } = useTranslation('features/deals');
  const pending = t('detail.facts.undefined');
  const payment = paymentOf(deal, t);
  const lastEventAt = deal.events
    .map((event) => event.timestamp)
    .sort()
    .at(-1);
  const lastContactDays = daysSince(lastEventAt);

  const facts: Fact[] = [
    {
      id: 'client',
      label: t('detail.facts.client'),
      value: deal.client_id ? (
        <Link
          to={`/app/agencies/${agencyId}/clients/${deal.client_id}`}
          className="text-(--text-primary) no-underline hover:text-(--seed-base)"
        >
          {names.clientName ?? deal.client_id}
        </Link>
      ) : pending,
    },
    {
      id: 'property',
      label: t('detail.facts.property'),
      value: deal.property_id ? (
        <Link
          to={`/app/agencies/${agencyId}/properties/${deal.property_id}`}
          className="text-(--text-primary) no-underline hover:text-(--seed-base)"
        >
          {names.propertyName ?? deal.property_id}
        </Link>
      ) : pending,
    },
    {
      id: 'type',
      label: t('detail.facts.type'),
      value: deal.deal_type
        ? (deal.deal_type === 'sale' ? t('types.sale') : t('types.rental'))
        : pending,
    },
    { id: 'origin', label: t('detail.facts.origin'), value: originLabel },
    { id: 'broker', label: t('detail.facts.broker'), value: names.brokerName ?? deal.broker_id },
    {
      id: 'openedAt',
      label: t('detail.facts.openedAt'),
      value: formatLocalizedDate(deal.created_at),
    },
  ];

  if (payment) {
    facts.push({ id: 'payment', label: t('detail.summary.paymentMethod'), value: payment });
  }
  facts.push({
    id: 'lastContact',
    label: t('detail.summary.lastContact'),
    value: lastContactDays == null
      ? pending
      : t('detail.timeline.daysAgo', { count: lastContactDays }),
  });

  return facts;
}

/**
 * The attendance at a glance: who, which property, what kind of deal,
 * where it came from, who is running it and for how long.
 *
 * A grid rather than a label/value list, because these are eight
 * independent answers a broker scans for one of — a column makes them
 * read as steps of something.
 *
 * @param props - The agency, the deal, its resolved names and origin label.
 * @returns The summary panel.
 */
export function DealSummaryPanel(props: DealSummaryPanelProps) {
  const { t } = useTranslation('features/deals');
  const facts = useFacts(props);

  return (
    <div className={cn(DEAL_PANEL_CLASS, 'gap-(--space-3)')} data-testid="deal-summary-panel">
      <DealPanelLabel>{t('detail.summary.panelTitle')}</DealPanelLabel>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,180px),1fr))] gap-(--space-4) gap-x-(--space-5)">
        {facts.map((fact) => (
          <div key={fact.id} data-testid={`deal-fact-${fact.id}`} className="flex min-w-0 flex-col gap-[3px]">
            <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
              {fact.label}
            </Text>
            <Text
              component="span"
              className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] leading-[var(--leading-snug)] text-(--text-primary)"
            >
              {fact.value}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

interface DealWantsPanelProps {
  agencyId: string;
  deal: Deal;
}

/**
 * What the client is looking for, as chips.
 *
 * Read off the client's own record, not the attendance: an attendance
 * carries only whether they mean to buy or to rent, and the search
 * profile — rooms, districts, ceiling — belongs to the person, who keeps
 * it across every attendance they open. The Editar link therefore opens
 * the client, and says so -- specifically onto the preferences section of
 * the edit form (`?edit=preferences`), since those chips are what the
 * broker clicked it to change.
 *
 * @param props - The agency and the attendance.
 * @returns The panel, or nothing when the client states no preferences.
 */
export function DealWantsPanel({ agencyId, deal }: DealWantsPanelProps) {
  const { t } = useTranslation('features/deals');
  const client = useDealClientRecord(agencyId, deal.client_id);
  const profile: Record<string, unknown> = client?.demand_profile ?? {};

  const chips = buildWantChips(profile, t);
  if (chips.length === 0) return null;

  return (
    <div className={cn(DEAL_PANEL_CLASS, 'gap-(--space-3)')} data-testid="deal-wants-panel">
      <DealPanelLabel
        accessory={(
          <Link
            to={`/app/agencies/${agencyId}/clients/${deal.client_id}?edit=preferences`}
            data-testid="deal-wants-edit"
            className="whitespace-nowrap text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--seed-base) no-underline hover:opacity-80"
          >
            {t('detail.wants.edit')}
          </Link>
        )}
      >
        {t('detail.wants.title')}
      </DealPanelLabel>

      <div className="flex flex-wrap gap-[6px]">
        {chips.map((chip) => (
          <Text
            key={chip}
            component="span"
            className="rounded-(--radius-md) border border-solid border-[color-mix(in_srgb,var(--seed-base)_15%,transparent)] bg-[color-mix(in_srgb,var(--seed-base)_6%,var(--surface-primary))] px-(--space-2-5) py-[3px] text-[length:var(--text-meta)] leading-[var(--leading-snug)] text-(--text-primary)"
          >
            {chip}
          </Text>
        ))}
      </div>
    </div>
  );
}

/**
 * Turns a client's demand profile into one chip per stated preference.
 *
 * @param profile - The client's `demand_profile`.
 * @param t - The deals-namespace translation function.
 * @returns The chip labels, in reading order.
 */
function buildWantChips(profile: Record<string, unknown>, t: TFunction): string[] {
  const chips: string[] = [];
  const money = (value: unknown) => Number(value).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL', maximumFractionDigits: 0,
  });

  if (profile.price_min || profile.price_max) {
    chips.push(t('detail.wants.priceRange', {
      from: money(profile.price_min ?? 0),
      to: money(profile.price_max ?? 0),
    }));
  }
  if (profile.bedrooms_min) chips.push(t('detail.wants.bedrooms', { count: Number(profile.bedrooms_min) }));
  if (profile.parking_min) chips.push(t('detail.wants.parking', { count: Number(profile.parking_min) }));
  if (profile.area_min) chips.push(t('detail.wants.area', { value: Number(profile.area_min) }));
  for (const key of ['cities', 'neighborhoods', 'property_kinds'] as const) {
    const list = profile[key];
    if (Array.isArray(list)) chips.push(...list.map(String));
  }
  return chips;
}
